import { getSession, clearSession } from './auth';

// Timezone normalization: map deprecated or alternate zone names to canonical IANA names.
// Example: "Asia/Calcutta" -> "Asia/Kolkata". Use `normalizeTimezone()` before sending
// timezone values to the server or when reading the browser timezone.
const TIMEZONE_ALIASES = {
    'asia/calcutta': 'Asia/Kolkata',
    'asia/culcatta': 'Asia/Kolkata',
    'asia/culctta': 'Asia/Kolkata',
    'asia/culcutta': 'Asia/Kolkata',
    'asia/kolkata': 'Asia/Kolkata',
    'india': 'Asia/Kolkata'
};

export const normalizeTimezone = (tz) => {
    if (!tz || typeof tz !== 'string') return tz;
    const key = tz.trim().toLowerCase();
    return TIMEZONE_ALIASES[key] || tz;
};

export const getNormalizedBrowserTimezone = () => {
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return normalizeTimezone(tz);
    } catch (e) {
        return undefined;
    }
};

const ODOO_CONFIG = {
    url: 'https://dash.xamta.co',
    db: 'e18_contract_portal'
};

const ensureLogin = async () => {
    const session = getSession();
    const currentUid = session?.uid;
    const token = localStorage.getItem('api_key') || session?.api_key || session?.token || session?.session_id;

    if (!currentUid || !token) {
        throw new Error('Not authenticated: Missing login session or api key');
    }
    return { ...session, api_key: token };
};

const cache = {
    partners: null,
    projects: null,
    lastFetch: {
        partners: 0,
        projects: 0
    }
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const rpcCall = async (endpoint, params) => {
    // Use relative path so the request goes to the React dev server 
    // and gets proxied to Odoo, bypassing CORS!
    const apiKey = localStorage.getItem('api_key') || getSession()?.api_key;
    const partnerId = getSession()?.partner_id;
    const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Api-Key': apiKey || ''
        },
        body: JSON.stringify({
            jsonrpc: '2.0',
            method: 'call',
            params: { ...params, partner_id: partnerId, api_key: apiKey },
            id: Math.floor(Math.random() * 10000)
        })
    });

    if (!response.ok) {
        throw new Error(`Server returned error ${response.status}: ${response.statusText}`);
    }

    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Invalid server response: Expected JSON but received ${contentType || 'text'}. This often indicates a server-side error (e.g. 502/504).`);
    }

    const data = await response.json();
    if (data.error) {
        const errorMsg = data.error.data?.message || data.error.message || 'Odoo RPC error';
        throw new Error(errorMsg);
    }

    return data.result;
};

const executeKw = async (model, method, args = [], kwargs = {}) => {
    const session = await ensureLogin();
    const currentUid = session.uid;
    const token = session.api_key;

    return await rpcCall('api/jsonrpc', {
        service: 'object',
        method: 'execute_kw',
        args: [ODOO_CONFIG.db, currentUid, token, model, method, args, kwargs]
    });
};

const searchRead = async (model, domain = [], fields = [], limit = 0, offset = 0, order = '') => {
    return await executeKw(model, 'search_read', [domain], { fields, limit, offset, order });
};


const searchCount = async (model, domain = []) => {
    return await executeKw(model, 'search_count', [domain], {});
};

/**
 * Fetch project details by ID
 */
export const getProjectDetails = async (id) => {
    try {
        const projects = await searchRead(
            'xmt.project',
            [['id', '=', parseInt(id)]],
            ['name', 'partner_id', 'user_id', 'description', 'date_start', 'date_end', 'task_count', 'create_date', 'state', 'budget'],
            1
        );
        return projects.length > 0 ? projects[0] : null;
    } catch (error) {
        console.error("Error fetching project:", error);
        throw error;
    }
};

/**
 * Fetch projects with search/pagination — filtered by logged-in user's partner_id
 */
export const getAllProjects = async (domain = [], limit = 80, offset = 0) => {
    try {
        const [projects, count] = await Promise.all([
            searchRead('xmt.project', domain, ['name', 'partner_id', 'description', 'date_start', 'date_end', 'task_count', 'state', 'budget'], limit, offset, 'create_date desc'),
            searchCount('xmt.project', domain)
        ]);
        return { projects, count };
    } catch (error) {
        console.error("getAllProjects", error);
        throw error;
    }
};

/**
 * Fetch payments — filtered by logged-in user
 */
export const getPayments = async (domain = [], limit = 80, offset = 0) => {
    try {
        const [payments, count] = await Promise.all([
            searchRead('xmt.payment', domain, ['payment_date', 'name', 'user_id', 'amount', 'status', 'payment_type'], limit, offset, 'payment_date desc, id desc'),
            searchCount('xmt.payment', domain)
        ]);
        return { payments, count };
    } catch (error) {
        console.error("getPayments", error);
        throw error;
    }
};
/**
 * Fetch tasks for a specific project */
export const getProjectTasks = async (domain = []) => {
    try {
        const tasks = await searchRead(
            'xmt.task',
            domain,
            ['name', 'date_deadline', 'description', 'state'],
            0,
            0,
            'create_date desc'
        );
        return tasks;
    } catch (error) {
        console.error("Error fetching tasks:", error);
        throw error;
    }
};

/**
 * Fetch timesheets — filtered by logged-in user
 */
export const getTimesheets = async (domain = [], limit = 80, offset = 0) => {
    try {
        const [timesheets, count] = await Promise.all([
            searchRead('xmt.timesheet', domain, ['name', 'create_date', 'date_start', 'task_id', 'duration', 'user_id', 'project_id'], limit, offset, 'create_date desc'),
            searchCount('xmt.timesheet', domain)
        ]);
        return { timesheets, count };
    } catch (error) {
        console.error("getTimesheets", error);
        throw error;
    }
};

/**
 * Fetch invoices — filtered by logged-in user's partner_id
 */
export const getInvoices = async (domain = [], limit = 80, offset = 0) => {
    try {
        const [invoices, count] = await Promise.all([
            searchRead('xmt.invoice', domain, ['name', 'partner_id', 'invoice_date', 'amount_total', 'state'], limit, offset, 'invoice_date desc'),
            searchCount('xmt.invoice', domain)
        ]);
        return { invoices, count };
    } catch (error) {
        console.error("getInvoices", error);
        throw error;
    }
};

/* ── Generic write/create helpers ── */

const odooWrite = async (model, id, data) => {
    return await executeKw(model, 'write', [[parseInt(id)], data], {});
};

export const odooCreate = async (model, data) => {
    return await executeKw(model, 'create', [data], {});
};

const odooUnlink = async (model, id) => {
    return await executeKw(model, 'unlink', [[parseInt(id)]], {});
};

/* ── Contracts (xmt.project) ── */
export const createProject = async (data) => {
    try { return await odooCreate('xmt.project', data); }
    catch (e) { console.error('createProject', e); throw e; }
};
export const updateProject = async (id, data) => {
    try { return await odooWrite('xmt.project', id, data); }
    catch (e) { console.error('updateProject', e); throw e; }
};

/* ── Timesheets (account.analytic.line) ── */
export const createTimesheet = async (data) => {
    try { return await odooCreate('xmt.timesheet', data); }
    catch (e) { console.error('createTimesheet', e); throw e; }
};
export const updateTimesheet = async (id, data) => {
    try { return await odooWrite('xmt.timesheet', id, data); }
    catch (e) { console.error('updateTimesheet', e); throw e; }
};

/* ── Payments (account.payment) ── */
export const createPayment = async (data) => {
    try { return await odooCreate('account.payment', data); }
    catch (e) { console.error('createPayment', e); throw e; }
};
export const updatePayment = async (id, data) => {
    try { return await odooWrite('account.payment', id, data); }
    catch (e) { console.error('updatePayment', e); throw e; }
};

/* ── Invoices (account.move) ── */
export const createInvoice = async (data) => {
    try { return await odooCreate('xmt.invoice', data); }
    catch (e) { console.error('createInvoice', e); throw e; }
};
export const updateInvoice = async (id, data) => {
    try { return await odooWrite('xmt.invoice', id, data); }
    catch (e) { console.error('updateInvoice', e); throw e; }
};

/* ── Tasks (xmt.task) ── */
export const createTask = async (data) => {
    try { return await odooCreate('xmt.task', data); }
    catch (e) { console.error('createTask', e); throw e; }
};
export const updateTask = async (id, data) => {
    try { return await odooWrite('xmt.task', id, data); }
    catch (e) { console.error('updateTask', e); throw e; }
};

export const searchPartners = async (query = '', forceRefresh = false) => {
    const now = Date.now();
    // Cache only if it's a generic search (no query)
    if (!query && !forceRefresh && cache.partners && (now - cache.lastFetch.partners < CACHE_DURATION)) {
        return cache.partners;
    }

    try {
        const domain = [['customer_rank', '>', 0]];
        if (query) {
            domain.push(['name', 'ilike', query]);
        }
        const results = await searchRead('res.partner', domain, ['name', 'display_name'], 100);

        if (!query) {
            cache.partners = results;
            cache.lastFetch.partners = now;
        }
        return results;
    } catch (e) {
        console.error('searchPartners', e);
        throw e;
    }
};
export const createPartner = async (name) => {
    return await odooCreate('res.partner', {
        name,
        customer_rank: 1
    });
};
/* ── Profile (res.users & res.partner) ── */

export const getUserProfile = async (userId) => {
    try {
        await ensureLogin();
        // 1. Get partner_id and share status from res.users
        const users = await searchRead('res.users', [['id', '=', parseInt(userId)]], ['partner_id', 'share'], 1);
        if (users.length === 0) return null;

        const userData = users[0];
        const partnerId = userData.partner_id[0];

        // 2. Get detailed partner info
        const partners = await searchRead(
            'res.partner',
            [['id', '=', partnerId]],
            ['name', 'street', 'street2', 'zip', 'city', 'email', 'country_id', 'state_id', 'phone', 'mobile', 'profile_type'],
            1
        );
        if (partners.length === 0) return null;

        return {
            ...partners[0],
            isPortal: userData.share
        };
    } catch (e) {
        // console.error('getUserProfile', e);
        throw e;
    }
};

export const updateUserProfile = async (partnerId, data) => {
    try {
        return await odooWrite('res.partner', partnerId, data);
    } catch (e) {
        console.error('updateUserProfile', e);
        throw e;
    }
};

export const updateUserDetails = async (userId, data) => {
    try {
        return await odooWrite('res.users', userId, data);
    } catch (e) {
        console.error('updateUserDetails', e);
        throw e;
    }
};

export const getCountries = async () => {
    try {
        return await searchRead('res.country', [], ['name', 'code'], 300, 0, 'name asc');
    } catch (e) {
        console.error('getCountries', e);
        throw e;
    }
};

export const getStates = async (countryId) => {
    try {
        const domain = countryId ? [['country_id', '=', parseInt(countryId)]] : [];
        return await searchRead('res.country.state', domain, ['name', 'code', 'country_id'], 1000, 0, 'name asc');
    } catch (e) {
        console.error('getStates', e);
        throw e;
    }
};

// Timezone and Language helpers moved here
export const getTimezones = async () => {
    try {
        const result = await executeKw('res.users', 'fields_get', [['tz']], {
            attributes: ['selection']
        });
        return result?.tz?.selection || [];
    } catch (e) {
        console.error('getTimezones Error:', e);
        return [];
    }
};

export const getLanguages = async () => {
    try {
        const result = await executeKw('res.users', 'fields_get', [['lang']], {
            attributes: ['selection']
        });
        return result?.lang?.selection || [];
    } catch (e) {
        console.error('getLanguages Error:', e);
        return [];
    }
};

/* ── Attachments (ir.attachment) ── */
export const getTaskAttachments = async (taskId) => {
    try {
        return await searchRead(
            'ir.attachment',
            [['res_model', '=', 'xmt.task'], ['res_id', '=', parseInt(taskId)]],
            ['name', 'mimetype', 'file_size', 'create_date'],
            0,
            0,
            'create_date desc'
        );
    } catch (e) {
        console.error('getTaskAttachments', e);
        throw e;
    }
};

export const uploadTaskAttachment = async (taskId, fileName, base64Data) => {
    try {
        return await odooCreate('ir.attachment', {
            name: fileName,
            datas: base64Data,
            res_model: 'xmt.task',
            res_id: parseInt(taskId),
            type: 'binary'
        });
    } catch (e) {
        console.error('uploadTaskAttachment', e);
        throw e;
    }
};


export const deleteAttachment = async (attachmentId) => {
    try {
        return await odooUnlink('ir.attachment', attachmentId);
    } catch (e) {
        console.error('deleteAttachment', e);
        throw e;
    }
};

export const GetUserDetails = async (userId) => {
    try {
        return await searchRead(
            'res.users',
            [['id', '=', parseInt(userId)]],
            ['lang', 'tz', 'profile_type'],
            1
        );
    } catch (e) {
        console.error('getTimesheetAttachments', e);
        throw e;
    }
};

/* ── Attendance (hr.attendance) ── */

/** Fetch attendance records with pagination */
export const getAttendanceRecords = async (domain = [], limit = 80, offset = 0) => {
    try {
        const [records, count] = await Promise.all([
            searchRead(
                'xmt.attendance',
                domain,
                ['checkin', 'checkout', 'type', 'checkin_note', 'checkout_note'],
                limit,
                offset,
                'checkin desc'
            ),
            searchCount('xmt.attendance', domain)
        ]);
        return { records, count };
    } catch (e) {
        console.error('getAttendanceRecords', e);
        throw e;
    }
};

/** Find today's open attendance record for an employee (checked-in but not checked-out) */
export const getOpenAttendance = async (userId) => {
    try {
        const results = await searchRead(
            'xmt.attendance',
            [['user_id', '=', parseInt(userId)], ['checkout', '=', false]],
            ['id', 'checkin', 'type', 'checkin_note', 'checkout_note'],
            1,
            0,
            'checkin desc'
        );
        return results.length > 0 ? results[0] : null;
    } catch (e) {
        console.error('getOpenAttendance', e);
        return null;
    }
};

/** Get employee linked to a user id.
 *  Strategy 1: match hr.employee.user_id directly
 *  Strategy 2: match hr.employee.name against res.users.name (admin fallback)
 *  Strategy 3: return the first employee found (so admin can always act)
 */
export const getEmployeeByUserId = async (userId) => {
    try {
        // Strategy 1 — direct link
        const direct = await searchRead(
            'hr.employee',
            [['user_id', '=', parseInt(userId)]],
            ['id', 'name'],
            1
        );
        if (direct.length > 0) return direct[0];

        // Strategy 2 — name match via res.users
        const users = await searchRead(
            'res.users',
            [['id', '=', parseInt(userId)]],
            ['name'],
            1
        );
        if (users.length > 0) {
            const userName = users[0].name;
            const byName = await searchRead(
                'hr.employee',
                [['name', 'ilike', userName]],
                ['id', 'name'],
                1
            );
            if (byName.length > 0) return byName[0];
        }

        // Strategy 3 (Removed) - Do not fallback to the first employee found.
        // Return null if no strictly linked employee is found.
        return null;
    } catch (e) {
        // console.error('getEmployeeByUserId', e);
        return null;
    }
};

/** Create a new check-in record */
export const checkIn = async (employeeId, userId, type = 'online_meeting', note = '') => {
    try {
        const ip = await fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => data.ip).catch(() => 'Unknown');
        const now = new Date();
        // Odoo expects UTC datetime string
        const checkInUtc = now.toISOString().replace('T', ' ').substring(0, 19);
        const newId = await odooCreate('xmt.attendance', {
            user_id: parseInt(userId),
            checkin: checkInUtc,
            type: type,
            checkin_note: note,
            checkin_ip: ip
        });
        return newId;
    } catch (e) {
        console.error('checkIn', e);
        throw e;
    }
};

/** Write checkout on an existing attendance record */
export const checkOut = async (attendanceId, note = '') => {
    try {
        const ip = await fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => data.ip).catch(() => 'Unknown');
        const now = new Date();
        const checkOutUtc = now.toISOString().replace('T', ' ').substring(0, 19);
        return await odooWrite('xmt.attendance', attendanceId, {
            checkout: checkOutUtc,
            checkout_note: note,
            checkout_ip: ip
        });
    } catch (e) {
        console.error('checkOut', e);
        throw e;
    }
};

/** Get total worked hours today for an employee */
export const getTodayWorkedHours = async (employeeId) => {
    try {
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const todayUtc = todayStart.toISOString().replace('T', ' ').substring(0, 19);
        const records = await searchRead(
            'xmt.attendance',
            [['employee_id', '=', parseInt(employeeId)], ['checkin', '>=', todayUtc]],
            ['worked_hours', 'checkin', 'checkout'],
            0, 0, 'checkin asc'
        );
        return records;
    } catch (e) {
        console.error('getTodayWorkedHours', e);
        return [];
    }
};


export default {
    getProjectDetails,
    GetUserDetails,
    getAllProjects,
    createProject,
    updateProject,
    getPayments,
    createPayment,
    updatePayment,
    getProjectTasks,
    createTask,
    updateTask,
    getTimesheets,
    createTimesheet,
    updateTimesheet,
    getInvoices,
    createInvoice,
    updateInvoice,
    searchPartners,
    getUserProfile,
    updateUserProfile,
    updateUserDetails,
    getCountries,
    getStates,
    getTimezones,
    getLanguages,
    getTaskAttachments,
    uploadTaskAttachment,
    deleteAttachment,
    getAttendanceRecords,
    getOpenAttendance,
    getEmployeeByUserId,
    checkIn,
    checkOut,
    getTodayWorkedHours
};
